<script setup>
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { type } = defineProps({
    type: {
        type: String,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href: route(
                        'front.projectManagement.dev.eCommerceModules',
                        {
                            tenant: props.row.original.id,
                        },
                    ),
                    inertia: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'cms',
            header: () => 'CMS',
            cell: (props) =>
                h(PillCell, {
                    pillValue:
                        props.row.original.cms === 'magento1'
                            ? 'Magento 1'
                            : props.row.original.cms === 'magento2'
                              ? 'Magento 2'
                              : props.row.original.cms === 'prestashop'
                                ? 'Prestashop'
                                : props.row.original.cms === 'wordpress'
                                  ? 'Wordpress'
                                  : props.row.original.cms === 'shopify'
                                    ? 'Shopify'
                                    : 'Custom',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'moduleNames',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Noms modules',
                }),
            cell: (props) =>
                props.row.original.eCommerceModules.length > 0
                    ? h(BaseCell, {}, [
                          h(
                              'ul',
                              {
                                  class: 'pl-6 list-disc',
                              },
                              props.row.original.eCommerceModules.map(
                                  (module) =>
                                      h('li', { innerText: module.name }),
                              ),
                          ),
                      ])
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'moduleVersions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Versions modules',
                }),
            cell: (props) =>
                props.row.original.eCommerceModules.length > 0
                    ? h(BaseCell, {}, [
                          h(
                              'ul',
                              {
                                  class: 'pl-6 list-disc',
                              },
                              props.row.original.eCommerceModules.map(
                                  (module) =>
                                      h('li', {
                                          innerText: module.version
                                              ? module.version
                                              : 'N/A',
                                      }),
                              ),
                          ),
                      ])
                    : null,
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.dev.eCommerceModules',
                                    {
                                        tenant: props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                            }),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.dev.postECommerceModules'),
    data: {
        type,
    },
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    />
</template>
